/**
 * Composant DailyVideoCall
 *
 * Intégration native Daily.co avec le SDK React pour une UI personnalisée
 * Utilisé dans le pilotage d'AG pour la visioconférence
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import DailyIframe, { DailyCall } from '@daily-co/daily-js'
import {
  DailyProvider,
  DailyVideo,
  useDaily,
  useLocalSessionId,
  useParticipantIds,
  useParticipantProperty,
  useScreenShare,
} from '@daily-co/daily-react'
import {
  Box,
  Flex,
  Text,
  HStack,
  Button,
  IconButton,
  Badge,
  Spinner,
  SimpleGrid,
} from '@chakra-ui/react'
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  PhoneOff,
  ScreenShare,
  ScreenShareOff,
  Users,
  Check,
  X,
  DoorOpen,
} from 'lucide-react'

// ===== TYPES =====

interface DailyVideoCallProps {
  roomUrl: string
  token: string
  userName: string
  onLeave: () => void
  onError?: (error: string) => void
}

interface ParticipantTileProps {
  sessionId: string
  isLocal?: boolean
  isLarge?: boolean
}

// ===== SINGLETON POUR GÉRER L'INSTANCE DAILY =====
// Daily.co n'autorise qu'une seule instance globale par défaut

// Instance unique et verrou pour éviter les race conditions
let sharedCallObject: DailyCall | null = null
let initializationPromise: Promise<DailyCall> | null = null

async function getSharedCallObject(): Promise<DailyCall> {
  // Si une initialisation est en cours, attendre qu'elle se termine
  if (initializationPromise) {
    console.log('Daily: Waiting for existing initialization...')
    return initializationPromise
  }

  // Si on a déjà une instance valide, la retourner
  if (sharedCallObject) {
    try {
      // Vérifier que l'instance est toujours valide
      sharedCallObject.meetingState()
      console.log('Daily: Reusing existing instance')
      return sharedCallObject
    } catch {
      // Instance invalide, on va en créer une nouvelle
      sharedCallObject = null
    }
  }

  // Créer une nouvelle instance avec un verrou
  initializationPromise = (async () => {
    try {
      console.log('Daily: Creating new call instance...')
      const callObj = DailyIframe.createCallObject({
        audioSource: true,  // Demander permissions audio
        videoSource: true,  // Demander permissions vidéo
      })
      sharedCallObject = callObj
      return callObj
    } finally {
      initializationPromise = null
    }
  })()

  return initializationPromise
}

async function leaveAndDestroy() {
  if (!sharedCallObject) return

  const callObj = sharedCallObject
  sharedCallObject = null

  try {
    const state = callObj.meetingState()
    console.log('Daily: Leaving and destroying, state:', state)
    if (state === 'joined-meeting' || state === 'joining-meeting') {
      await callObj.leave()
    }
    callObj.destroy()
  } catch (e) {
    console.warn('Daily: Error during cleanup:', e)
  }
}

// ===== COMPOSANT PRINCIPAL =====

export function DailyVideoCall({
  roomUrl,
  token,
  userName,
  onLeave,
  onError,
}: DailyVideoCallProps) {
  const [callObject, setCallObject] = useState<DailyCall | null>(null)
  const [isJoining, setIsJoining] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const isMountedRef = useRef(true)
  const hasInitializedRef = useRef(false)

  // Créer et rejoindre le call
  useEffect(() => {
    if (!roomUrl || !token) return

    // Éviter les doubles initialisations
    if (hasInitializedRef.current) {
      return
    }
    hasInitializedRef.current = true
    isMountedRef.current = true

    const initCall = async () => {
      try {
        setIsJoining(true)
        setError(null)

        // Récupérer ou créer l'instance Daily (avec gestion mutex)
        const callObj = await getSharedCallObject()

        // Vérifier si déjà dans un meeting
        const currentState = callObj.meetingState()
        console.log('Daily: Current state:', currentState)

        if (currentState === 'joined-meeting') {
          console.log('Daily: Already in a meeting')
          if (isMountedRef.current) {
            setCallObject(callObj)
            setIsJoining(false)
          }
          return
        }

        // Si en cours de connexion, attendre
        if (currentState === 'joining-meeting') {
          console.log('Daily: Already joining, waiting...')
          await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Timeout')), 15000)
            const checkState = () => {
              const state = callObj.meetingState()
              if (state === 'joined-meeting') {
                clearTimeout(timeout)
                resolve()
              } else if (state === 'left-meeting' || state === 'error') {
                clearTimeout(timeout)
                reject(new Error('Failed to join'))
              } else {
                setTimeout(checkState, 200)
              }
            }
            checkState()
          })
          if (isMountedRef.current) {
            setCallObject(callObj)
            setIsJoining(false)
          }
          return
        }

        // Créer une promesse qui se résout quand on rejoint le meeting
        const joinedPromise = new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Timeout de connexion (30s)'))
          }, 30000)

          const handleJoined = () => {
            console.log('Daily: joined-meeting event received')
            clearTimeout(timeout)
            resolve()
          }

          const handleError = (event: { errorMsg?: string }) => {
            console.error('Daily error:', event?.errorMsg || event)
            clearTimeout(timeout)
            reject(new Error(event?.errorMsg || 'Erreur de connexion'))
          }

          // Écouter les événements une seule fois pour la connexion
          callObj.once('joined-meeting', handleJoined)
          callObj.once('error', handleError)
        })

        // Handler pour quitter le meeting (permanent)
        callObj.on('left-meeting', () => {
          console.log('Daily: Left meeting')
          if (isMountedRef.current) {
            onLeave()
          }
        })

        // Lancer le join (ne pas attendre sa promesse, utiliser l'événement)
        console.log('Daily: Joining room...')
        callObj.join({
          url: roomUrl,
          token: token,
          userName: userName,
          startVideoOff: true,
          startAudioOff: true,
        })

        // Attendre l'événement joined-meeting
        await joinedPromise
        console.log('Daily: Join successful, enabling devices...')

        // Tenter d'activer les périphériques
        try {
          await callObj.setLocalAudio(true)
          await callObj.setLocalVideo(true)
        } catch (deviceErr) {
          console.warn('Daily: Could not enable devices:', deviceErr)
        }

        if (isMountedRef.current) {
          setCallObject(callObj)
          setIsJoining(false)
        }
      } catch (err) {
        console.error('Error joining call:', err)
        if (isMountedRef.current) {
          const errorMsg = err instanceof Error ? err.message : 'Impossible de rejoindre'
          setError(errorMsg)
          onError?.(errorMsg)
          setIsJoining(false)
        }
        hasInitializedRef.current = false
      }
    }

    initCall()

    // Cleanup
    return () => {
      isMountedRef.current = false
    }
  }, [roomUrl, token, userName, onLeave, onError])

  // Cleanup - ne pas détruire automatiquement, seulement réinitialiser le flag
  // La destruction se fait uniquement via le bouton "Quitter"
  useEffect(() => {
    return () => {
      hasInitializedRef.current = false
      // Ne PAS appeler leaveAndDestroy() ici car StrictMode remonte le composant
    }
  }, [])

  // État de chargement
  if (isJoining) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        h="full"
        bg="gray.900"
        gap={4}
      >
        <Spinner size="lg" color="white" />
        <Text color="white" fontSize="sm">
          Connexion à la visioconférence...
        </Text>
      </Flex>
    )
  }

  // État d'erreur
  if (error || !callObject) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        h="full"
        bg="gray.900"
        gap={4}
        p={4}
      >
        <VideoOff size={48} color="#EF4444" />
        <Text color="red.400" fontSize="sm" textAlign="center">
          {error || 'Erreur de connexion'}
        </Text>
        <Button size="sm" colorPalette="gray" onClick={onLeave}>
          Fermer
        </Button>
      </Flex>
    )
  }

  // Rendu avec le provider Daily
  return (
    <DailyProvider callObject={callObject}>
      <VideoCallUI onLeave={onLeave} />
    </DailyProvider>
  )
}

// ===== UI DU CALL =====

function VideoCallUI({ onLeave }: { onLeave: () => void }) {
  const daily = useDaily()
  const localSessionId = useLocalSessionId()
  const remoteParticipantIds = useParticipantIds({ filter: 'remote' })
  const allParticipantIds = useParticipantIds()
  const { isSharingScreen, startScreenShare, stopScreenShare } = useScreenShare()
  // État local pour les participants en attente (plus fiable que le hook)
  const [waitingParticipants, setWaitingParticipants] = useState<Array<{ id: string; name: string }>>([])

  // Écouter les événements de knocking
  useEffect(() => {
    if (!daily) return

    const handleWaitingParticipantAdded = (event: { participant: { id: string; name: string } }) => {
      console.log('Daily: Waiting participant added:', event.participant)
      setWaitingParticipants(prev => {
        // Éviter les doublons
        if (prev.some(p => p.id === event.participant.id)) return prev
        return [...prev, { id: event.participant.id, name: event.participant.name || 'Participant' }]
      })
    }

    const handleWaitingParticipantUpdated = (event: { participant: { id: string; name: string } }) => {
      console.log('Daily: Waiting participant updated:', event.participant)
      setWaitingParticipants(prev =>
        prev.map(p => p.id === event.participant.id
          ? { ...p, name: event.participant.name || p.name }
          : p
        )
      )
    }

    const handleWaitingParticipantRemoved = (event: { participant: { id: string } }) => {
      console.log('Daily: Waiting participant removed:', event.participant)
      setWaitingParticipants(prev => prev.filter(p => p.id !== event.participant.id))
    }

    daily.on('waiting-participant-added', handleWaitingParticipantAdded)
    daily.on('waiting-participant-updated', handleWaitingParticipantUpdated)
    daily.on('waiting-participant-removed', handleWaitingParticipantRemoved)

    return () => {
      daily.off('waiting-participant-added', handleWaitingParticipantAdded)
      daily.off('waiting-participant-updated', handleWaitingParticipantUpdated)
      daily.off('waiting-participant-removed', handleWaitingParticipantRemoved)
    }
  }, [daily])

  // Debug
  useEffect(() => {
    console.log('Daily: Waiting participants state:', waitingParticipants)
  }, [waitingParticipants])

  // États audio/vidéo locaux
  const [isMicOn, setIsMicOn] = useState(true)
  const [isCamOn, setIsCamOn] = useState(true)

  // Toggle micro
  const toggleMic = useCallback(() => {
    if (!daily) return
    daily.setLocalAudio(!isMicOn)
    setIsMicOn(!isMicOn)
  }, [daily, isMicOn])

  // Toggle caméra
  const toggleCam = useCallback(() => {
    if (!daily) return
    daily.setLocalVideo(!isCamOn)
    setIsCamOn(!isCamOn)
  }, [daily, isCamOn])

  // Toggle partage d'écran
  const toggleScreenShare = useCallback(() => {
    if (isSharingScreen) {
      stopScreenShare()
    } else {
      startScreenShare()
    }
  }, [isSharingScreen, startScreenShare, stopScreenShare])

  // Admettre un participant en attente
  const admitParticipant = useCallback((id: string) => {
    if (!daily) return
    console.log('Daily: Admitting participant', id)
    daily.updateWaitingParticipant(id, { grantRequestedAccess: true })
  }, [daily])

  // Refuser un participant en attente
  const denyParticipant = useCallback((id: string) => {
    if (!daily) return
    console.log('Daily: Denying participant', id)
    daily.updateWaitingParticipant(id, { grantRequestedAccess: false })
  }, [daily])

  // Admettre tous les participants en attente
  const admitAll = useCallback(() => {
    if (!daily || !waitingParticipants.length) return
    console.log('Daily: Admitting all participants')
    waitingParticipants.forEach((p) => {
      daily.updateWaitingParticipant(p.id, { grantRequestedAccess: true })
    })
  }, [daily, waitingParticipants])

  // Quitter le call et détruire l'instance
  const handleLeave = useCallback(async () => {
    await leaveAndDestroy()
    onLeave()
  }, [onLeave])

  // Déterminer le layout
  const hasRemoteParticipants = remoteParticipantIds.length > 0
  const totalParticipants = allParticipantIds.length
  const hasWaitingParticipants = waitingParticipants.length > 0

  return (
    <Flex direction="column" h="full" bg="gray.900">
      {/* Zone vidéos */}
      <Box flex={1} p={2} overflow="hidden">
        {!hasRemoteParticipants ? (
          // Seul dans le call - afficher sa propre vidéo en grand
          <Flex h="full" align="center" justify="center">
            {localSessionId && (
              <Box w="full" maxW="600px" h="full" maxH="400px">
                <ParticipantTile sessionId={localSessionId} isLocal isLarge />
              </Box>
            )}
          </Flex>
        ) : totalParticipants <= 2 ? (
          // 2 participants - layout côte à côte
          <Flex h="full" gap={2}>
            {remoteParticipantIds.map((id) => (
              <Box key={id} flex={1}>
                <ParticipantTile sessionId={id} isLarge />
              </Box>
            ))}
            {localSessionId && (
              <Box position="absolute" bottom={4} right={4} w="150px" h="100px" zIndex={10}>
                <ParticipantTile sessionId={localSessionId} isLocal />
              </Box>
            )}
          </Flex>
        ) : (
          // Plus de 2 - grille
          <SimpleGrid columns={totalParticipants <= 4 ? 2 : 3} gap={2} h="full">
            {localSessionId && (
              <ParticipantTile sessionId={localSessionId} isLocal />
            )}
            {remoteParticipantIds.map((id) => (
              <ParticipantTile key={id} sessionId={id} />
            ))}
          </SimpleGrid>
        )}
      </Box>

      {/* Panneau des participants en attente */}
      {hasWaitingParticipants && (
        <Box
          bg="orange.900"
          borderTopWidth="1px"
          borderColor="orange.700"
          p={3}
        >
          <HStack justify="space-between" mb={2}>
            <HStack>
              <DoorOpen size={18} color="orange" />
              <Text color="white" fontSize="sm" fontWeight="medium">
                {waitingParticipants.length} participant{waitingParticipants.length > 1 ? 's' : ''} en attente
              </Text>
            </HStack>
            {waitingParticipants.length > 1 && (
              <Button size="xs" colorPalette="green" onClick={admitAll}>
                <Check size={14} />
                Admettre tous
              </Button>
            )}
          </HStack>
          <Flex gap={2} flexWrap="wrap">
            {waitingParticipants.map((participant) => (
              <HStack
                key={participant.id}
                bg="gray.800"
                borderRadius="md"
                px={3}
                py={2}
                gap={2}
              >
                <Text color="white" fontSize="sm">
                  {participant.name || 'Participant'}
                </Text>
                <IconButton
                  aria-label="Admettre"
                  size="xs"
                  colorPalette="green"
                  variant="solid"
                  onClick={() => admitParticipant(participant.id)}
                >
                  <Check size={14} />
                </IconButton>
                <IconButton
                  aria-label="Refuser"
                  size="xs"
                  colorPalette="red"
                  variant="solid"
                  onClick={() => denyParticipant(participant.id)}
                >
                  <X size={14} />
                </IconButton>
              </HStack>
            ))}
          </Flex>
        </Box>
      )}

      {/* Barre de contrôles */}
      <HStack
        p={3}
        bg="gray.800"
        justify="center"
        gap={2}
        borderTopWidth="1px"
        borderColor="gray.700"
      >
        {/* Compteur participants + attente */}
        <HStack color="gray.400" mr={4}>
          <Users size={16} />
          <Text fontSize="sm">{totalParticipants}</Text>
          {hasWaitingParticipants && (
            <Badge colorPalette="orange" variant="solid" fontSize="xs">
              +{waitingParticipants.length}
            </Badge>
          )}
        </HStack>

        {/* Bouton micro */}
        <IconButton
          aria-label={isMicOn ? 'Couper le micro' : 'Activer le micro'}
          size="md"
          borderRadius="full"
          colorPalette={isMicOn ? 'gray' : 'red'}
          variant={isMicOn ? 'outline' : 'solid'}
          onClick={toggleMic}
        >
          {isMicOn ? <Mic size={20} /> : <MicOff size={20} />}
        </IconButton>

        {/* Bouton caméra */}
        <IconButton
          aria-label={isCamOn ? 'Couper la caméra' : 'Activer la caméra'}
          size="md"
          borderRadius="full"
          colorPalette={isCamOn ? 'gray' : 'red'}
          variant={isCamOn ? 'outline' : 'solid'}
          onClick={toggleCam}
        >
          {isCamOn ? <Video size={20} /> : <VideoOff size={20} />}
        </IconButton>

        {/* Bouton partage écran */}
        <IconButton
          aria-label={isSharingScreen ? 'Arrêter le partage' : 'Partager l\'écran'}
          size="md"
          borderRadius="full"
          colorPalette={isSharingScreen ? 'green' : 'gray'}
          variant={isSharingScreen ? 'solid' : 'outline'}
          onClick={toggleScreenShare}
        >
          {isSharingScreen ? <ScreenShareOff size={20} /> : <ScreenShare size={20} />}
        </IconButton>

        {/* Séparateur */}
        <Box w="1px" h={8} bg="gray.600" mx={2} />

        {/* Bouton quitter */}
        <Button
          size="md"
          colorPalette="red"
          borderRadius="full"
          onClick={handleLeave}
        >
          <PhoneOff size={18} />
          Quitter
        </Button>
      </HStack>

      {/* Audio est géré automatiquement par DailyProvider */}
    </Flex>
  )
}

// ===== TUILE PARTICIPANT =====

function ParticipantTile({ sessionId, isLocal = false, isLarge = false }: ParticipantTileProps) {
  const [userName, videoState, audioState] = useParticipantProperty(sessionId, [
    'user_name',
    'tracks.video.state',
    'tracks.audio.state',
  ])

  const hasVideo = videoState === 'playable'
  const hasAudio = audioState === 'playable'

  return (
    <Box
      position="relative"
      w="full"
      h="full"
      minH={isLarge ? '200px' : '80px'}
      bg="gray.800"
      borderRadius="lg"
      overflow="hidden"
    >
      {/* Vidéo */}
      {hasVideo ? (
        <DailyVideo
          sessionId={sessionId}
          type="video"
          automirror={isLocal}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : (
        <Flex
          position="absolute"
          inset={0}
          align="center"
          justify="center"
          bg="gray.800"
        >
          <Box
            w={isLarge ? 20 : 12}
            h={isLarge ? 20 : 12}
            borderRadius="full"
            bg="gray.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize={isLarge ? '2xl' : 'lg'} color="white" fontWeight="bold">
              {(userName as string)?.charAt(0)?.toUpperCase() || '?'}
            </Text>
          </Box>
        </Flex>
      )}

      {/* Overlay infos */}
      <HStack
        position="absolute"
        bottom={2}
        left={2}
        right={2}
        justify="space-between"
      >
        {/* Nom */}
        <Badge
          bg="blackAlpha.700"
          color="white"
          fontSize="xs"
          px={2}
          py={0.5}
          borderRadius="md"
        >
          {isLocal ? 'Vous' : (userName as string) || 'Participant'}
        </Badge>

        {/* Indicateurs */}
        <HStack gap={1}>
          {!hasAudio && (
            <Box bg="red.500" p={1} borderRadius="full">
              <MicOff size={12} color="white" />
            </Box>
          )}
          {!hasVideo && (
            <Box bg="red.500" p={1} borderRadius="full">
              <VideoOff size={12} color="white" />
            </Box>
          )}
        </HStack>
      </HStack>
    </Box>
  )
}

export default DailyVideoCall
